import { Fragment, useId } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { X } from "lucide-react";
import { classNames } from "../../helpers/classNames";

/**
 * A sheet that slides up from the bottom on mobile and tablet (grab handle,
 * rounded top) and is a centred dialog on desktop. Used for the cover-ready
 * pop-up and the dedication editor (docs/design/HANDOFF.md).
 *
 * `title` is the visible serif heading; `actions` go at the bottom.
 *
 * The slide and fade are Headless UI's CSS transitions, not framer-motion, for
 * the same reason as `Modal`: framer-motion's end-of-animation handoff lands a
 * frame late on a busy page and the sheet flashes.
 */
const Sheet = ({ open, onClose, title, children, actions = null, label, className }) => {
  const titleId = useId();
  return (
    <Dialog as={Fragment} open={open} onClose={() => onClose?.()}>
      <div className="fixed inset-0 z-[70]">
        <DialogBackdrop
          transition
          className="absolute inset-0 bg-[rgba(27,13,18,0.5)] transition-opacity duration-200 ease-out data-[closed]:opacity-0"
          aria-hidden="true"
        />
        <div className="absolute inset-0 flex items-end justify-center lg:items-center lg:p-6">
          <DialogPanel
            transition
            aria-labelledby={titleId}
            aria-label={label}
            className={classNames(
              "flex max-h-[92vh] w-full flex-col bg-white shadow-[0_-12px_40px_rgba(27,13,18,0.25)]",
              "rounded-t-[20px] lg:max-w-[520px] lg:rounded-3xl lg:shadow-[0_24px_60px_rgba(27,13,18,0.3)]",
              "transition duration-200 ease-out data-[closed]:translate-y-10 data-[closed]:opacity-0",
              className
            )}
          >
            <div className="flex justify-center pt-2 lg:hidden" aria-hidden="true">
              <span className="h-[5px] w-10 rounded-full bg-md-control" />
            </div>
            <div className="flex justify-end px-2">
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="flex h-11 w-11 items-center justify-center rounded-md text-md-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col items-center gap-4 overflow-y-auto px-6 pb-2 text-center">
              {title ? (
                <h2 id={titleId} className="m-0 font-heading text-[28px] font-normal leading-[1.1] text-md-dark">
                  {title}
                </h2>
              ) : null}
              {children}
            </div>
            {actions ? (
              <div className="flex flex-shrink-0 flex-col gap-1 px-6 pb-[max(30px,env(safe-area-inset-bottom))] pt-3 lg:pb-7">{actions}</div>
            ) : null}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default Sheet;
